import queryString from 'query-string'

import { SelectOptionProps } from '@src/quince-components/src/atoms/select/Select'
import { ProductOptionDisplayType } from '@src/quince-site-common/src/domains/ProductOption'
import getColorFromUrl from '@src/quince-site-common/src/utils/extractColorFromURL'
import { mapValues } from '@src/utils/functions/utils'

export interface IFilterConfig {
  readonly columnsToShow: number
  readonly label: string
  readonly type: string
}

export interface IProductTypeFilter {
  readonly title: string
  readonly slug: string
}

export class FilterConfig {
  readonly columnsToShow: number
  readonly label: string
  readonly type: string

  constructor({ columnsToShow, label, type }: IFilterConfig) {
    this.columnsToShow = columnsToShow
    this.label = label
    this.type = type
    return {
      ...this,
    }
  }
}

export interface IFilter {
  readonly name: string
  readonly values: (FilterOptions | string)[]
  readonly config: FilterConfig
}

export interface UpdateQueryParams {
  filter?:
    | { type: 'reset' }
    | { type: 'remove'; name: FilterKeys; value: string }
    | { type: 'toggle'; name: FilterKeys; value: string; label: string }
  sort?: { type: 'reset' } | { type: 'toggle'; value: string }
}

export class Filter implements IFilter {
  readonly name: string
  readonly values: (FilterOptions | string)[]
  readonly config: FilterConfig

  constructor({ name, values, config }: IFilter) {
    this.name = name
    this.values = values
    this.config = config
    return {
      ...this,
    }
  }

  static makeSelectFilterOptionFromValue(value: FilterOptions | string): SelectOptionProps {
    if (typeof value == 'string') {
      return {
        label: value,
        value: value,
      }
    } else {
      return FilterOptions.makeSelectFilterOption(value)
    }
  }
}

export interface IFilterOption {
  readonly name: string
  readonly value: string
  readonly displayType: ProductOptionDisplayType
  readonly hex?: string
  readonly imgURL?: string
}

export class FilterOptions implements IFilterOption {
  readonly name: string
  readonly value: string
  readonly displayType: ProductOptionDisplayType
  readonly hex: string | undefined = undefined
  readonly imgURL: string | undefined = undefined

  constructor({ name, value, displayType, hex = undefined, imgURL = undefined }: IFilterOption) {
    this.name = name
    this.value = value
    this.displayType = displayType
    this.hex = hex
    this.imgURL = imgURL
    return {
      ...this,
    }
  }

  static makeSelectFilterOption(filterOptions: FilterOptions): SelectOptionProps {
    return {
      label: filterOptions.name,
      value: filterOptions.value || filterOptions.name,
      hex: filterOptions.hex,
      imgURL: filterOptions.imgURL,
    }
  }
}

export interface ISelectedFilter {
  readonly colors?: string[]
  readonly sizes?: string[]
  readonly prices?: string[]
  readonly materials?: string[]
  readonly styles?: string[]
  readonly constructions?: string[]
  readonly fabrics?: string[]
  // readonly sort?: string[]
  readonly pile_height?: string[]
  readonly category_sweater?: string[]
  readonly categories?: string[]
  readonly departments?: string[]
  readonly jacket_styles?: string[]
  readonly stone_shapes?: string[]
  readonly diamond_types?: string[]
  readonly productType?: string[]
  readonly lengths?: string[]
  readonly warmths?: string[]
}
export type FilterKeys = keyof ISelectedFilter

export class SelectedFilter implements ISelectedFilter {
  readonly colors?: string[] | undefined
  readonly sizes?: string[] | undefined
  readonly prices?: string[] | undefined
  readonly materials?: string[] | undefined
  readonly styles?: string[] | undefined
  readonly constructions?: string[] | undefined
  readonly fabrics?: string[] | undefined
  // readonly sort?: string[] | undefined
  readonly pile_height?: string[] | undefined
  readonly category_sweater?: string[] | undefined
  readonly categories?: string[] | undefined
  readonly departments?: string[] | undefined
  readonly jacket_styles?: string[] | undefined
  readonly stone_shapes?: string[] | undefined
  readonly diamond_types?: string[] | undefined
  readonly productType?: string[] | undefined // Intentionally not assigned as it is always a slug filter.
  readonly lengths?: string[] | undefined
  readonly warmths?: string[] | undefined

  constructor({
    colors,
    sizes,
    prices,
    materials,
    styles,
    constructions,
    fabrics,
    // sort,
    pile_height,
    category_sweater,
    categories,
    departments,
    stone_shapes,
    diamond_types,
    jacket_styles,
    lengths,
    warmths,
  }: ISelectedFilter) {
    this.colors = colors
    this.sizes = sizes
    this.prices = prices
    this.materials = materials
    this.styles = styles
    this.constructions = constructions
    this.fabrics = fabrics
    // this.sort = sort
    this.pile_height = pile_height
    this.category_sweater = category_sweater
    this.categories = categories
    this.departments = departments
    this.stone_shapes = stone_shapes
    this.diamond_types = diamond_types
    this.jacket_styles = jacket_styles
    this.lengths = lengths
    this.warmths = warmths
    return {
      ...this,
    }
  }

  static isAnyFilterSelected(selectedFilter: SelectedFilter) {
    return !!FILTER_KEYS.find(x =>
      SelectedFilter.isFieldPresent(SelectedFilter.getSelectedFilterValuesByFilterName(selectedFilter, x)),
    )
  }

  private static isFieldPresent(selectedField: string[] | undefined): boolean {
    return !!(selectedField && selectedField.length > 0)
  }

  public static getSelectedValuesAsList(selectedFilter: SelectedFilter): string[] {
    return FILTER_KEYS.map(key => SelectedFilter.getSelectedFilterValuesByFilterName(selectedFilter, key)).flat()
  }

  public static getSelectedFilterValuesByFilterName(
    selectedFilter: SelectedFilter,
    filterName: FilterKeys,
    shouldEncode = false,
  ): string[] {
    const selectedFilterList = selectedFilter[filterName] ?? []

    if (shouldEncode) {
      return selectedFilterList.map((filter: string) => encodeURIComponent(filter))
    }

    return selectedFilterList
  }

  public static removeValueFromSelectedFilter(selectedFilter: SelectedFilter, value: string, filterName?: FilterKeys) {
    if (filterName) {
      const currentFilterValues = SelectedFilter.getSelectedFilterValuesByFilterName(selectedFilter, filterName)
      const updatedFilterValues = currentFilterValues.filter(item => item !== value)
      return new SelectedFilter({
        ...selectedFilter,
        [filterName]: updatedFilterValues,
      })
    } else {
      const sanitizedFilter = mapValues(selectedFilter, (arr?: any[]) => {
        const filteredArray = arr?.filter(x => x !== value)
        return filteredArray?.length === 0 ? undefined : filteredArray
      })
      return new SelectedFilter(sanitizedFilter)
    }
  }

  public static isFilterValueSelected(selectedFilter: SelectedFilter, filterName: FilterKeys, value: string) {
    const currentFilterValues = SelectedFilter.getSelectedFilterValuesByFilterName(selectedFilter, filterName)
    return currentFilterValues.includes(value)
  }

  public static addValueToSelectedFilter(selectedFilter: SelectedFilter, filterName: FilterKeys, value: string) {
    const updatedFilterValues = [
      ...SelectedFilter.getSelectedFilterValuesByFilterName(selectedFilter, filterName),
      value,
    ]

    return new SelectedFilter({
      ...selectedFilter,
      [filterName]: updatedFilterValues,
    })
  }

  public static toggleValueFromSelectedFilter(selectedFilter: SelectedFilter, filterName: FilterKeys, value: string) {
    const currentFilterValues = SelectedFilter.getSelectedFilterValuesByFilterName(selectedFilter, filterName)
    if (currentFilterValues.includes(value)) {
      return SelectedFilter.removeValueFromSelectedFilter(selectedFilter, value, filterName)
    } else {
      return SelectedFilter.addValueToSelectedFilter(selectedFilter, filterName, value)
    }
  }

  public static getUpdatedSelectedFilterStringForWindowURL(selectedFilter: SelectedFilter) {
    const filterString = FILTER_KEYS.map(key => {
      const values = SelectedFilter.getSelectedFilterValuesByFilterName(selectedFilter, key, true)
      if (values.length > 0) {
        return `${key}=${values.join(',')}`
      }
    })
      .filter(x => x)
      .join('&')
    return filterString
  }

  public static getSelectedFilterFromQueryParams(filter: string): SelectedFilter {
    const params = queryString.parse(filter)
    const selectedFilter = new SelectedFilter({})
    FILTER_KEYS.forEach(filterKey => {
      const filterValues = params[filterKey]
      if (filterValues) {
        selectedFilter[filterKey] = Array.isArray(filterValues) ? filterValues : filterValues.split(',')
      }
    })
    return selectedFilter
  }

  public static checkEquality(left: SelectedFilter, right: SelectedFilter): boolean {
    return FILTER_KEYS.every(filterKey => left[filterKey] === right[filterKey])
  }

  public static getColorFromUrl(): string {
    const {
      location: { pathname },
    } = window
    return getColorFromUrl(pathname)
  }
}

export const FILTER_KEYS: FilterKeys[] = [
  'colors',
  'sizes',
  'prices',
  'materials',
  'styles',
  'constructions',
  'fabrics',
  // 'sort',
  'pile_height',
  'category_sweater',
  'categories',
  'departments',
  'stone_shapes',
  'diamond_types',
  'jacket_styles',
  'lengths',
  'warmths',
]
